import QRCode from 'qrcode';

// Base URL for QR code links - you can modify this as needed
// Example URLs:
// - For local development: 'http://localhost:3000/question'
// - For production: 'https://your-domain.com/question'
// - For mobile app deep links: 'yourapp://question'
const BASE_QR_URL =  "https://motionco.sng.link/Bpn50/wczr";

/**
 * Generate QR code for a question
 * @param {Object} question - The question object
 * @param {string} questionId - The question ID
 * @returns {Promise<string>} - Base64 encoded QR code image
 */
export const generateQRCode = async (question, questionId) => {
  try {
    // Create URL for the question
    const questionUrl = `${BASE_QR_URL}?_dl=motionscheme://home&pcn=${questionId}`;
    
    // Generate QR code as base64 string
    const qrCodeDataURL = await QRCode.toDataURL(questionUrl, {
      width: 100,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
    
    return qrCodeDataURL;
  } catch (error) {
    console.error('Error generating QR code:', error);
    return null;
  }
};

/**
 * Save QR code image to localStorage with question ID as key
 * @param {string} questionId - The question ID
 * @param {string} qrCodeDataURL - Base64 encoded QR code image
 */
export const saveQRCodeToStorage = (questionId, qrCodeDataURL) => {
  try {
    const qrCodeKey = `qr_code_${questionId}`;
    localStorage.setItem(qrCodeKey, qrCodeDataURL);
  } catch (error) {
    console.error('Error saving QR code to storage:', error);
  }
};

/**
 * Retrieve QR code image from localStorage
 * @param {string} questionId - The question ID
 * @returns {string|null} - Base64 encoded QR code image or null if not found
 */
export const getQRCodeFromStorage = (questionId) => {
  try {
    const qrCodeKey = `qr_code_${questionId}`;
    return localStorage.getItem(qrCodeKey);
  } catch (error) {
    console.error('Error retrieving QR code from storage:', error);
    return null;
  }
};

/**
 * Get or generate QR code for a question
 * @param {Object} question - The question object
 * @param {string} questionId - The question ID
 * @returns {Promise<string|null>} - Base64 encoded QR code image
 */
export const getOrGenerateQRCode = async (question, questionId) => {
  // First, try to get existing QR code from storage
  let qrCodeDataURL = getQRCodeFromStorage(questionId);
  
  // If not found, generate new QR code
  if (!qrCodeDataURL) {
    qrCodeDataURL = await generateQRCode(question, questionId);
    
    // Save the generated QR code to storage
    if (qrCodeDataURL) {
      saveQRCodeToStorage(questionId, qrCodeDataURL);
    }
  }
  
  return qrCodeDataURL;
};

/**
 * Clear all QR codes from storage (useful for cleanup)
 */
export const clearAllQRCodes = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('qr_code_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing QR codes:', error);
  }
};

/**
 * Get QR code URL for a question (without generating the image)
 * @param {string} questionId - The question ID
 * @returns {string} - The URL that the QR code will point to
 */
export const getQRCodeURL = (questionId) => {
  return `${BASE_QR_URL}?_dl=motionscheme://home&pcn=${questionId}`;
};
